import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import BlockIcon from '@mui/icons-material/BlockOutlined'
import {
  dateHeureBoutique,
  instantDepuisHeureMurale,
  type Blocage,
  type Category,
} from '@iobiz/shared'
import { api } from '../../api/client.js'
import { ChampDateHeure } from '../../ui/ChampDateHeure.js'
import { runCycle } from '../../data/sync/engine.js'

/**
 * Fermer des POSTES à la réservation, sur une période.
 *
 * ⭐ Le besoin vient du planning réel : une formation mobilise deux fauteuils
 * sur trois pendant une semaine. La boutique veut continuer à prendre des
 * rendez-vous sur le troisième — ce qu'un « jour fermé » ne sait pas dire.
 *
 * ⚠️ On choisit COMBIEN de postes ferment, jamais LESQUELS. Désigner un
 * fauteuil parmi des fauteuils interchangeables obligerait à réaffecter les
 * rendez-vous déjà posés dessus pour libérer celui qu'on vient de bloquer.
 *
 * ⚠️ Le blocage vaut PARTOUT — site public et comptoir. Une fermeture que le
 * logiciel laisserait contourner d'un clic n'en serait pas une.
 *
 * ⚠️ Les heures saisies sont celles de la BOUTIQUE, comme dans tout le reste de
 * l'agenda. Lues dans le fuseau du poste, « 10 h » posé depuis Paris fermait
 * 9 h à Douala : la fermeture tombait à côté des créneaux qu'elle visait.
 */

export function BlocagesDialog({
  open,
  storeId,
  categories,
  fuseau,
  pas,
  onClose,
}: {
  open: boolean
  storeId: string
  /** Toutes les catégories ; seules celles de SERVICES peuvent être bloquées. */
  categories: Category[]
  /** Le fuseau de la BOUTIQUE — référence de toutes les heures saisies ici. */
  fuseau: string
  /** Le pas de la grille, en minutes — Paramètres › Agenda. */
  pas: number
  onClose: () => void
}): JSX.Element {
  const services = categories.filter((c) => c.kind === 'service')
  /** Valeur stable : c'est l'identifiant qui compte, pas le tableau. */
  const premierService = services[0]?.id ?? ''

  const [liste, setListe] = useState<Blocage[] | null>(null)
  const [categoryId, setCategoryId] = useState('')
  const [debut, setDebut] = useState('')
  const [fin, setFin] = useState('')
  const [postes, setPostes] = useState('1')
  const [motif, setMotif] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  /**
   * ⚠️ Fenêtre de lecture LARGE — un an en arrière, deux en avant. Un blocage
   * posé pour l'été prochain doit rester visible aujourd'hui, sans quoi on le
   * repose une seconde fois en croyant l'avoir oublié.
   */
  const charger = useCallback(async () => {
    const an = 365 * 24 * 3600 * 1000
    const du = new Date(Date.now() - an).toISOString()
    const au = new Date(Date.now() + 2 * an).toISOString()
    try {
      const r = await api.get<{ data: Blocage[] }>(
        `/stores/${storeId}/blocages?du=${encodeURIComponent(du)}&au=${encodeURIComponent(au)}`,
      )
      setListe(r.data)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Lecture impossible.')
      setListe([])
    }
  }, [storeId])

  useEffect(() => {
    if (!open) return
    setErreur(null)
    setCategoryId(premierService)
    setDebut('')
    setFin('')
    setPostes('1')
    setMotif('')
    void charger()
    // ⚠️ `services` est recalculé à chaque rendu : le surveiller relancerait la
    // lecture en boucle. Seules l'ouverture et `charger` comptent ici.
  }, [open, charger, premierService])

  /**
   * ⭐ Les heures se posent au PAS DE LA GRILLE, celui de Paramètres › Agenda.
   *
   * Une fermeture qui commence à 10 h 07 ferme la moitié d'un créneau que
   * personne ne peut réserver : la grille ne fait rien commencer entre deux
   * pas. Le champ propose donc les mêmes heures que l'agenda, ni plus fines ni
   * plus larges.
   *
   * ⚠️ Vérifié ICI aussi, et pas seulement par l'attribut `step` : celui-ci
   * contraint le sélecteur du navigateur, mais une heure TAPÉE au clavier le
   * traverse sans être corrigée.
   *
   * ⚠️ On compte depuis MINUIT, le navigateur depuis le 1ᵉʳ janvier 1970. Les
   * deux ne coïncident que si le pas divise la journée — ce qui est vrai de
   * tous ceux que proposent les réglages (10, 15, 20, 30, 45, 60). Un pas qui
   * ne la diviserait pas ferait diverger le champ et cette vérification.
   */
  function aligne(valeur: string): boolean {
    if (valeur === '') return true
    const [h, m] = valeur.slice(11, 16).split(':')
    return (Number(h) * 60 + Number(m)) % pas === 0
  }

  const messagePas = `Les heures vont par ${pas} min — le pas de l’agenda`

  const valide =
    categoryId !== '' &&
    debut !== '' &&
    fin !== '' &&
    fin > debut &&
    Number(postes) >= 1 &&
    aligne(debut) &&
    aligne(fin)

  /** Combien de postes porte une catégorie — au moins un, même sans poste déclaré. */
  function capaciteDe(id: string): number {
    return Math.max(categories.find((c) => c.id === id)?.postes?.length ?? 0, 1)
  }

  const capacite = capaciteDe(categoryId)
  const restants = capacite - Number(postes)

  /**
   * « 2026-10-21T10:00 » → instant, à l'horloge de la BOUTIQUE.
   *
   * ⚠️ `new Date(valeur)` aurait lu la saisie dans le fuseau du poste : c'est
   * exactement l'erreur que cette fonction existe pour empêcher.
   */
  function versInstant(valeur: string): string {
    return instantDepuisHeureMurale(valeur.slice(0, 10), valeur.slice(11, 16), fuseau)
  }

  async function poser(): Promise<void> {
    setEnCours(true)
    setErreur(null)
    try {
      await api.post(`/stores/${storeId}/blocages`, {
        categoryId,
        debut: versInstant(debut),
        fin: versInstant(fin),
        postes: Number(postes),
        ...(motif.trim() ? { motif: motif.trim() } : {}),
      })
      setDebut('')
      setFin('')
      setMotif('')
      await charger()
      await rapatrier()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement impossible.')
    } finally {
      setEnCours(false)
    }
  }

  /**
   * ⚠️ Un cycle de synchronisation APRÈS chaque écriture.
   *
   * Le blocage part par appel REST, pas par la file d'attente : la réplique ne
   * l'apprend qu'au prochain tirage. Sans ce rappel, la grille de l'agenda
   * derrière la fenêtre restait inchangée pendant une minute, et l'on reposait
   * le blocage en croyant l'avoir raté.
   */
  async function rapatrier(): Promise<void> {
    try {
      await runCycle(storeId)
    } catch {
      // Le blocage EST enregistré : un tirage manqué n'est pas un échec de
      // l'écriture, et le cycle suivant le rattrapera.
    }
  }

  async function lever(id: string): Promise<void> {
    try {
      await api.delete(`/stores/${storeId}/blocages/${id}`)
      await charger()
      await rapatrier()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Suppression impossible.')
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Blocages de réservation</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 4, pt: 4 }}>
        {services.length === 0 ? (
          <Alert severity="info">
            Aucune catégorie de services. Un blocage ferme des postes, et les postes
            appartiennent aux catégories de services — créez-en une depuis le catalogue.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Fermez un nombre de postes sur une période. Le créneau cesse d’être proposé sur le
              site — et le comptoir ne peut plus y réserver — dès qu’il ne reste plus une seule
              place ouverte. Les heures sont celles de la boutique, au pas de l’agenda ({pas} min).
            </Typography>

            <TextField
              select
              label="Catégorie de services"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              fullWidth
            >
              {services.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name} — {c.postes?.length ?? 0} poste
                  {(c.postes?.length ?? 0) > 1 ? 's' : ''}
                </MenuItem>
              ))}
            </TextField>

            {/* ⭐ Le calendrier de l'application, pas celui du navigateur —
                le même que partout ailleurs, avec l'horloge en plus. Son pas
                est celui de la grille : le sélecteur n'offre que des heures
                réservables. */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <ChampDateHeure
                label="Du"
                value={debut}
                onChange={setDebut}
                pas={pas}
                error={!aligne(debut)}
                helperText={!aligne(debut) ? messagePas : ' '}
                sx={{ flex: 1, minWidth: 210 }}
              />
              <ChampDateHeure
                label="Au"
                value={fin}
                onChange={setFin}
                pas={pas}
                // ⚠️ La fin ne descend pas sous le début : le calendrier le
                // dit avant la validation, au lieu de refuser après coup.
                {...(debut !== '' ? { min: new Date(debut) } : {})}
                error={(fin !== '' && debut !== '' && fin <= debut) || !aligne(fin)}
                helperText={
                  fin !== '' && debut !== '' && fin <= debut
                    ? 'La fin doit suivre le début'
                    : !aligne(fin)
                      ? messagePas
                      : ' '
                }
                sx={{ flex: 1, minWidth: 210 }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Postes bloqués"
                type="number"
                value={postes}
                onChange={(e) => setPostes(e.target.value)}
                inputProps={{ min: 1, max: 100 }}
                helperText={`sur ${capacite}`}
                sx={{ width: 150 }}
              />
              <TextField
                label="Motif"
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                placeholder="Formation, congé, travaux…"
                sx={{ flex: 1, minWidth: 190 }}
              />
            </Box>

            {/* ⚠️ Dit ce qu'il RESTE, dans les deux sens. Fermer deux postes
                sur trois laisse le créneau réservable, et c'est le but ; mais
                un gérant qui croyait fermer l'heure entière doit le lire ici,
                pas le découvrir sur le site en pleine semaine de formation. */}
            {Number(postes) >= 1 &&
              (restants > 0 ? (
                <Alert
                  severity="info"
                  action={
                    <Button color="inherit" size="small" onClick={() => setPostes(String(capacite))}>
                      Fermer tout
                    </Button>
                  }
                >
                  Il restera {restants} poste{restants > 1 ? 's' : ''} ouvert
                  {restants > 1 ? 's' : ''} sur {capacite} : le créneau continuera d’être proposé à
                  la réservation.
                </Alert>
              ) : (
                <Alert severity="warning">
                  Vous fermez {Number(postes)} poste{Number(postes) > 1 ? 's' : ''} sur {capacite} :
                  plus aucune réservation ne sera possible sur cette catégorie pendant la période.
                </Alert>
              ))}

            <Button variant="contained" startIcon={<BlockIcon />} disabled={!valide || enCours} onClick={poser}>
              {enCours ? 'Enregistrement…' : 'Bloquer'}
            </Button>
          </>
        )}

        {erreur && <Alert severity="error">{erreur}</Alert>}

        <Divider />

        <Typography variant="subtitle2">Blocages en place</Typography>
        {liste === null ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Chargement…
          </Typography>
        ) : liste.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            Aucun blocage.
          </Typography>
        ) : (
          liste.map((b) => (
            <Box key={b.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {b.categoryName ?? 'Catégorie supprimée'} — {b.postes} poste
                  {b.postes > 1 ? 's' : ''} sur {capaciteDe(b.categoryId)}
                  {b.postes < capaciteDe(b.categoryId) ? ' (réservation encore possible)' : ''}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  Du {dateHeureBoutique(b.debut, fuseau)} au {dateHeureBoutique(b.fin, fuseau)}
                  {b.motif ? ` · ${b.motif}` : ''}
                </Typography>
              </Box>
              <IconButton aria-label="Lever ce blocage" onClick={() => void lever(b.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))
        )}
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  )
}
